import {LIGHT_THEME as theme} from '@apps-common/ui-theme/Themes.js';
import MultiPoint from 'ol/geom/MultiPoint';
import {fromExtent} from 'ol/geom/Polygon';
import {transformExtent} from 'ol/proj';
import {Circle, Fill, Stroke, Style} from 'ol/style';
import CircleStyle from 'ol/style/Circle';

const STROKE_WIDTH = 2.5;

// In order for layer clipping to be applied, a style must be set. This adds a
// transparent stroke to the clip geometry.
export const CLIP_STYLE = new Style({
  stroke: new Stroke({
    width: 1,
    color: 'rgba(0,0,0,0)',
  }),
  fill: new Fill({
    color: 'rgba(0,0,0,0)',
  }),
});
/**
 * Same function as OL util but with our colors
 * https://github.com/openlayers/openlayers/blob/v6.1.1/src/ol/style/Style.js#L474
 * */
export const createDrawingStyle = (drawing, dashed) => {
  const styles = {};

  styles.Polygon = [
    new Style({
      fill: new Fill({
        color: drawing
          ? 'rgba(255, 255, 255, 0.5)'
          : 'rgba(255, 255, 255, 0.2)',
      }),
      stroke: drawing
        ? null
        : new Stroke({
            color: 'white',
            width: STROKE_WIDTH,
          }),
    }),
  ];

  styles.MultiPolygon = styles.Polygon;

  styles.LineString = [
    new Style({
      stroke: new Stroke({
        color: drawing ? 'white' : 'rgba(0,0,0,0.3)',
        width: STROKE_WIDTH + 2,
      }),
    }),
    new Style({
      stroke: new Stroke({
        color: drawing ? theme.palette.primary.dark : 'white',
        width: STROKE_WIDTH,
        lineDash: dashed ? [10, 10] : null,
      }),
    }),
  ];
  styles.MultiLineString = styles.LineString;

  styles.Circle = styles.Polygon.concat(styles.LineString);

  styles.MultiPoint = styles.Point;

  styles.GeometryCollection = styles.Polygon.concat(
    styles.LineString,
    styles.Point
  );

  return styles;
};

export const getDefaultStyleFunction = () => {
  const styles = createDrawingStyle(false);

  return function (feature) {
    return styles[feature.getGeometry().getType()];
  };
};

export const getDefaultDrawFunction = dashed => {
  const styles = createDrawingStyle(true, dashed);

  return function (feature) {
    return styles[feature.getGeometry().getType()];
  };
};

export const getVertexStyle = radius => ({
  image: new Circle({
    radius,
    fill: new Fill({
      color: 'white',
    }),
    stroke: new Stroke({
      color: theme.palette.primary.main,
      width: 2,
    }),
  }),
});

export const hoverStyle = [
  new Style({
    stroke: new Stroke({
      color: '#00000055',
      width: STROKE_WIDTH + 2,
    }),
    fill: new Fill({
      color: `${theme.palette.primary.main}11`,
    }),
  }),
  new Style({
    stroke: new Stroke({
      color: theme.palette.primary.main,
      width: STROKE_WIDTH,
    }),
  }),
];

export const modifyStyle = feature => {
  const points = new MultiPoint(
    feature
      .getGeometry()
      .getCoordinates()
      .flatMap(ring => ring)
  );

  return [
    ...hoverStyle,
    new Style({
      ...getVertexStyle(5),
      geometry: () => points,
    }),
  ];
};

const geometryAtRestStyle = [
  new Style({
    stroke: new Stroke({
      color: 'rgba(0,0,0,0.3)',
      width: STROKE_WIDTH * 2,
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.01)',
    }),
  }),
  new Style({
    stroke: new Stroke({
      color: 'white',
      width: STROKE_WIDTH,
    }),
  }),
];

// Style of the geometry layer at rest
export const geometryStyle = targetId => {
  return feature => {
    const {visible, hover} = feature.getProperties();
    if (visible === false) {
      return [];
    }
    if (feature.getId() === targetId) {
      return modifyStyle(feature);
    } else if (hover === true) {
      return hoverStyle;
    } else {
      return geometryAtRestStyle;
    }
  };
};

export const footprintStyle = {
  'fill-color': 'rgba(0,0,0,0.25)',
  'stroke-color': 'rgba(255,255,255,0.75)',
  'stroke-width': 1,
};

export const mosaicGeometryStyle = (feature, resolution) => {
  // AOIs come in as multipolygons. Using the extent
  //  makes it easier to determine a representative point.
  // These don't need to be that precise, they are just meant
  //  to give the user a clue where their data is located when
  //  they are zoomed out too far for it be meaningful.
  const extent = feature.getGeometry().getExtent();

  const webMercExtent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');

  const pxWidth = (webMercExtent[2] - webMercExtent[0]) / resolution;
  const pxHeight = (webMercExtent[3] - webMercExtent[1]) / resolution;
  // minimum number of rendered pixel dimensions to show
  // before switching to the point renderer.
  const pxThreshold = 50;

  const innerColor = 'rgba(0, 157, 165, .5)';
  const outerColor = 'rgba(0, 157, 165, .75)';

  // if either the width or height is below the threshold, then
  //  render it as a point/marker type style instead of a polygon.
  if (pxWidth < pxThreshold || pxHeight < pxThreshold) {
    return new Style({
      geometry: fromExtent(extent).getInteriorPoint(),
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({
          color: innerColor,
        }),
        stroke: new Stroke({
          color: outerColor,
        }),
      }),
    });
  } else {
    return [
      new Style({
        stroke: new Stroke({
          width: 3,
          color: innerColor,
        }),
      }),
      new Style({
        stroke: new Stroke({
          width: 1,
          color: outerColor,
        }),
      }),
    ];
  }
};
